import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton
} from '@mui/material';
import { LocationOn, Close } from '@mui/icons-material';

const LocationPermissionDialog = ({ open, onClose, onRetry }) => {
    const [deviceInfo, setDeviceInfo] = useState({
        isIOS: false,
        isAndroid: false,
        isChrome: false,
        isSafari: false
    });

    useEffect(() => {
        // Detect device and browser
        const userAgent = navigator.userAgent.toLowerCase();
        setDeviceInfo({
            isIOS: /ipad|iphone|ipod/.test(userAgent),
            isAndroid: /android/.test(userAgent),
            isChrome: /chrome/.test(userAgent) && !/edge|edg/.test(userAgent),
            isSafari: /safari/.test(userAgent) && !/chrome|chromium|edg/.test(userAgent)
        });
    }, []);

    const { isIOS, isAndroid, isChrome, isSafari } = deviceInfo;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn color="primary" />
                    <Typography>Enable Location Access</Typography>
                </Box>
                <IconButton
                    edge="end"
                    onClick={onClose}
                    size="small"
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    {isIOS ? (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Please follow these steps:
                            </Typography>
                            <Box sx={{ ml: 1 }}>
                                <Typography variant="body1" paragraph>
                                    1. Open Settings<br />
                                    2. Scroll down and tap on this browser<br />
                                    3. Tap Location<br />
                                    4. Select "Allow While Using App"
                                </Typography>
                            </Box>
                        </>
                    ) : isAndroid && isChrome ? (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Please follow these steps on Chrome for Android:
                            </Typography>
                            <Box sx={{ ml: 1 }}>
                                <Typography variant="body1" paragraph>
                                    1. Tap the <strong>three-dot menu</strong> in the top right<br />
                                    2. Go to <strong>Settings &gt; Site Settings &gt; Location</strong><br />
                                    3. Find this site and select "Allow"
                                </Typography>
                            </Box>
                        </>
                    ) : isSafari ? (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Please follow these steps on Safari:
                            </Typography>
                            <Box sx={{ ml: 1 }}>
                                <Typography variant="body1" paragraph>
                                    1. Click <strong>Safari &gt; Settings</strong> in the top menu bar<br />
                                    2. Go to <strong>Websites &gt; Location</strong><br />
                                    3. Find this site and set it to "Allow"
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography variant="subtitle2" gutterBottom>
                                Please follow these steps:
                            </Typography>
                            <Box sx={{ ml: 1 }}>
                                <Typography variant="body1" paragraph>
                                    1. Click the <strong>lock icon 🔒</strong> in your address bar<br />
                                    2. Find "Location"<br />
                                    3. Select "Allow"<br />
                                    4. Refresh this page
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{
                px: 3,
                pb: 2,
                borderTop: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={onRetry}
                    sx={{ mb: 1 }}
                >
                    Refresh Page
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 1 }}
                    onClick={onClose}
                >
                    Manually
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LocationPermissionDialog;